import React, { useEffect, useState } from 'react'
import {
  BlockingLoader,
  Container,
  TextBody,
  TextTitle,
  ToastContext,
  Tools
} from '@pdrj/pmi-iqos-ds-react'
import {
  cookie,
  parseTranslations,
  PublishedComponent,
  CommonTranslations,
  setTranslations,
  htmlTranslation,
  TierStatus,
  LayoutContainer,
  ProfileNoDevice,
  ProfileTierCard,
  Routes,
  goTo,
  mapProfileInfo
} from '@pdrj/pmi-iqos-shared-react'
import {
  getInitialPropsMock,
  buildProfileInfoData,
  buildTiersStaticInfo
} from '@iqosphere-common/utils'
import { IqosphereApiManager } from '@iqosphere-common/services'
import QrTierCard from './qr-tier-card'
import ProfileMembershipCardViewTranslations from './profile-membership-card-view.json'

const ProfileMembershipCardViewComponent = ({ translations, tiers, line }) => {
  const api = new IqosphereApiManager(line)

  const [loading, setLoading] = useState(true)
  const [profileInfo, setProfileInfo] = useState(null)
  const [profileName, setProfileName] = useState(null)
  const [cardQr, setCardQr] = useState(null)
  const [tierStaticInfo, setTierStaticInfo] = useState(null)
  const [tierStatus, setTierStatus] = useState(null)

  if (translations) {
    setTranslations(translations)
  }

  const popupToast = ToastContext.getContextActions().popupToast

  useEffect(() => {
    loadData()
  }, [])

  useEffect(() => {
    async function fetchData() {
      const { membership } = await api.profile.getProfile()
      const status = membership && membership.status
      const tiersStaticInfo = buildTiersStaticInfo(tiers)
      setTierStatus(status)
      setTierStaticInfo(
        tiersStaticInfo &&
          status &&
          tiersStaticInfo.filter(tier => tier.type === status)[0]
      )
      setLoading(false)
    }
    fetchData()
    const loyaltyCookieContent = cookie.getLoyaltyProfile()
    const p = loyaltyCookieContent && loyaltyCookieContent.p
    const profileInfoData = buildProfileInfoData(p)
    setProfileInfo(profileInfoData)
  }, [])

  useEffect(() => {
    async function fetchData() {
      const profileInfoRes = cookie.getProfileInfoCookie()
      const profileInfo = mapProfileInfo(profileInfoRes)
      const profileName = profileInfo.lastName + ' ' + profileInfo.firstName
      setProfileName(profileName)
    }
    fetchData()
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      const res = await api.iqosphereProfile.getMembershipCardQrImage()
      const { cardQrUrl } = buildMembershipCardQrImageData(res)
      setCardQr(cardQrUrl)
    } catch (ex) {
      popupToast({
        type: ToastContext.types.error,
        subtitle: translations.tk_error
      })
    } finally {
      setLoading(false)
    }
  }

  const buildMembershipCardQrImageData = membershipCardImageResponse => {
    return {
      cardThumbQr: membershipCardImageResponse.imageUrl,
      cardQrUrl: membershipCardImageResponse.imageThumbnail
    }
  }

  return (
    <Container position={Container.positions.relative}>
      {loading && <BlockingLoader loading />}
      <LayoutContainer xMainLayout={!Tools.isDesktop()}>
        <Container pt={['8', '0']}>
          <TextTitle heading={TextTitle.headings.h2}>
            {translations.tk_membershipCardTitle}
          </TextTitle>
        </Container>
      </LayoutContainer>
      {!(profileInfo && profileInfo.hasDevicesRegistered) ? (
        <LayoutContainer xMainLayout={!Tools.isDesktop()}>
          <Container pt={['8', '12']} pb="8">
            <ProfileNoDevice
              translations={translations}
              onAddDeviceClickHandler={() => {
                goTo(Routes.profileDevices, true)
              }}
            />
          </Container>
        </LayoutContainer>
      ) : (
        <>
          <Container pt="4">
            <LayoutContainer xMainLayout={!Tools.isDesktop()}>
              <TextBody
                {...htmlTranslation(
                  translations,
                  'tk_membershipCardViewDescription'
                )}
              />
            </LayoutContainer>
          </Container>
          {tierStaticInfo && (
            <Container pt={('12', '24')}>
              {tierStatus === TierStatus.NO_TIER ? (
                <LayoutContainer xMainLayout={!Tools.isDesktop()}>
                  <ProfileTierCard
                    translations={translations}
                    tierStatus={tierStatus}
                    tier={tierStaticInfo}
                    fullName={profileName}
                    date={profileInfo && profileInfo.tierExpirationDate}
                  />
                </LayoutContainer>
              ) : (
                <Container position={Container.positions.relative}>
                  <Container
                    position={Container.positions.absolute}
                    l="0"
                    r="0"
                    t="0"
                    pt="64">
                    <Container pt="24">
                      <Container
                        height="96"
                        bg={Container.backgrounds.turquoise}
                      />
                    </Container>
                  </Container>
                  <LayoutContainer xMainLayout={!Tools.isDesktop()}>
                    <Container pb={['48', '24']}>
                      <Container>
                        <QrTierCard
                          translations={translations}
                          tierStaticInfo={tierStaticInfo}
                          qrImage={cardQr}
                          fullName={profileName}
                          date={profileInfo && profileInfo.tierExpirationDate}
                        />
                      </Container>
                    </Container>
                  </LayoutContainer>
                </Container>
              )}
            </Container>
          )}
        </>
      )}
    </Container>
  )
}

const ProfileMembershipCardView = props => {
  return (
    <ToastContext>
      <PublishedComponent
        innerComponent={ProfileMembershipCardViewComponent}
        fullWidth
        {...ComponentDefaultProps}
        {...props}
      />
    </ToastContext>
  )
}

const ComponentTranslations = {
  ...ProfileMembershipCardViewTranslations,
  ...CommonTranslations
}

const ComponentDefaultProps = {
  componentName: 'ProfileMembershipCardView',
  translations: {
    ...parseTranslations(ComponentTranslations, true)
  }
}

ProfileMembershipCardView.getInitialProps = async req => {
  return PublishedComponent.getGlobalInitialProps({
    context: req && req.query && req.query.context,
    line: !!req?.query?.line,
    componentName: ComponentDefaultProps.componentName,
    defaultTranslations: ComponentDefaultProps.translations,
    translationsConfig: ComponentTranslations,
    getSpecificInitialProps: async () => {
      const tiers =
        req && req.query && req.query.mock === 'true'
          ? getInitialPropsMock('profileTiersStaticInfo')
          : (req.body.extra && req.body.extra.tiers) || null
      return {
        tiers
      }
    }
  })
}

export default ProfileMembershipCardView
